import React from "react";
import { Link, useParams } from "react-router-dom";
import axios from "axios";
import ReactMarkdown from "react-markdown";
import { Loader2, AlertCircle, MapPin, ArrowLeft, Calendar } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const PUBLIC_BASE = process.env.REACT_APP_BACKEND_URL || "";

/**
 * v23.19 — Single blog post page with schema.org JSON-LD for Google rich results.
 */
export default function BlogPostPage() {
  const { slug } = useParams();
  const [post, setPost] = React.useState(null);
  const [loading, setLoading] = React.useState(true);
  const [error, setError] = React.useState("");

  React.useEffect(() => {
    let cancelled = false;
    setLoading(true);
    (async () => {
      try {
        const { data } = await axios.get(`${API}/blog/${slug}`);
        if (cancelled) return;
        setPost(data);
      } catch (e) {
        if (!cancelled) setError(e.response?.status === 404 ? "Post not found." : "Failed to load post.");
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [slug]);

  // SEO — set title, meta, canonical, JSON-LD when post loads
  React.useEffect(() => {
    if (!post) return;
    document.title = `${post.title} · Raanah`;
    const setMeta = (name, content, attr = "name") => {
      let el = document.querySelector(`meta[${attr}="${name}"]`);
      if (!el) {
        el = document.createElement("meta");
        el.setAttribute(attr, name);
        document.head.appendChild(el);
      }
      el.setAttribute("content", content);
    };
    setMeta("description", post.meta_description || "");
    setMeta("keywords", (post.keywords || []).join(", "));
    setMeta("og:title", post.title, "property");
    setMeta("og:description", post.meta_description || "", "property");
    setMeta("og:type", "article", "property");
    if (post.hero_image_url) setMeta("og:image", post.hero_image_url, "property");
    setMeta("twitter:card", "summary_large_image");

    // Canonical link
    let canonical = document.querySelector('link[rel="canonical"]');
    if (!canonical) {
      canonical = document.createElement("link");
      canonical.setAttribute("rel", "canonical");
      document.head.appendChild(canonical);
    }
    canonical.setAttribute("href", post.canonical_url || `${window.location.origin}/blog/${post.slug}`);

    // JSON-LD Article schema
    let jsonld = document.querySelector('script[data-testid="blog-jsonld"]');
    if (!jsonld) {
      jsonld = document.createElement("script");
      jsonld.type = "application/ld+json";
      jsonld.setAttribute("data-testid", "blog-jsonld");
      document.head.appendChild(jsonld);
    }
    jsonld.textContent = JSON.stringify({
      "@context": "https://schema.org",
      "@type": "Article",
      "headline": post.title,
      "description": post.meta_description,
      "image": post.hero_image_url ? [post.hero_image_url] : undefined,
      "datePublished": post.published_at,
      "dateModified": post.updated_at,
      "author": { "@type": "Organization", "name": "Raanah" },
      "publisher": { "@type": "Organization", "name": "Raanah", "url": PUBLIC_BASE },
      "mainEntityOfPage": { "@type": "WebPage", "@id": `${window.location.origin}/blog/${post.slug}` },
    });
  }, [post]);

  if (loading) {
    return (
      <div className="min-h-screen bg-[#FAFAF7] flex items-center justify-center">
        <Loader2 size={22} className="animate-spin text-[#1E4D55]" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-[#FAFAF7] flex flex-col items-center justify-center p-6">
        <AlertCircle size={22} className="text-[#B91C1C]" />
        <p className="mt-3 text-sm text-[#B91C1C]" data-testid="blog-post-error">{error}</p>
        <Link to="/blog" className="mt-4 text-sm underline text-[#1E4D55]">← Back to blog</Link>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#FAFAF7]" data-testid="blog-post-page">
      <header className="border-b border-[#0A0A0A]/10 bg-white">
        <div className="max-w-3xl mx-auto px-6 py-6 flex items-center justify-between">
          <Link to="/" className="font-display text-xl font-bold tracking-tight text-[#0A0A0A]">Raanah</Link>
          <Link to="/blog" className="text-sm text-[#475569] hover:text-[#0A0A0A] inline-flex items-center gap-1"><ArrowLeft size={13} /> All posts</Link>
        </div>
      </header>

      <article className="max-w-3xl mx-auto px-6 py-14" data-testid={`blog-post-${post.slug}`}>
        <div className="flex items-center gap-3 flex-wrap text-[11px] uppercase tracking-[0.18em] font-bold">
          <span className="text-[#1E4D55]">{(post.category || "").replace(/_/g, " ")}</span>
          {post.city && (
            <span className="text-[#475569] inline-flex items-center gap-1">
              <MapPin size={11} /> {post.city}
            </span>
          )}
          {post.published_at && (
            <span className="text-[#475569] inline-flex items-center gap-1">
              <Calendar size={11} /> {new Date(post.published_at).toLocaleDateString("en-GB", { day: "numeric", month: "long", year: "numeric" })}
            </span>
          )}
        </div>

        <h1 className="mt-4 font-display text-4xl sm:text-5xl font-bold tracking-tight text-[#0A0A0A] leading-tight" data-testid="blog-post-title">
          {post.title}
        </h1>

        <p className="mt-4 text-lg text-[#475569] leading-relaxed">{post.meta_description}</p>

        {post.hero_image_url && (
          <img src={post.hero_image_url} alt={post.title} className="mt-8 w-full h-auto border border-[#0A0A0A]/10" loading="eager" />
        )}

        <div className="prose prose-lg max-w-none mt-10 text-[#0A0A0A]" data-testid="blog-post-body">
          <ReactMarkdown>{post.body_markdown || ""}</ReactMarkdown>
        </div>

        <div className="mt-16 pt-8 border-t border-[#0A0A0A]/10 text-sm text-[#475569]">
          <p>Need a Luton van to move your stuff? Raanah connects you with verified drivers across the UK — book in 60 seconds.</p>
          <Link to="/" className="mt-3 inline-block bg-[#1E4D55] hover:bg-[#163C42] text-white px-5 py-3 text-sm font-semibold">Get a quote →</Link>
        </div>
      </article>

      <footer className="border-t border-[#0A0A0A]/10 bg-white mt-16">
        <div className="max-w-3xl mx-auto px-6 py-8 text-xs text-[#475569] flex items-center justify-between">
          <span>© {new Date().getFullYear()} Raanah</span>
          <Link to="/blog" className="hover:text-[#0A0A0A]">More posts →</Link>
        </div>
      </footer>
    </div>
  );
}
